import {
    Assessment,
    Issue,
    LinkRegistry
} from "../../API";
import {
    Box,
    BoxProps,
    Button,
    Typography,
    styled
} from '@mui/material';

import { IssueFilter } from "../../lib/dashboard/types/IssueFilter";
import { useMemo } from 'react';

interface NoIssuesOrAssessmentsMessageProps {
    propertyLinkRegistries: Array<LinkRegistry>;
    propertyIssues: Array<Issue>;
    propertyAssessments: Array<Assessment>;
    locationIdsFilter: Array<string>;
    setFilter: (filter: IssueFilter) => void;
}

interface PropertyHasItemsHiddenByFilterProps {
    setFilter: (filter: IssueFilter) => void;
}

interface LocationHasItemsHiddenByFilterProps {
    setFilter: (filter: IssueFilter) => void;
}

export const NoIssuesOrAssessmentsMessage = ({
    propertyLinkRegistries,
    propertyIssues,
    propertyAssessments,
    locationIdsFilter,
    setFilter
}: NoIssuesOrAssessmentsMessageProps) => {
    const locationIdToLinkRegistryMap = useMemo<Map<string, Array<LinkRegistry>>>(() => {
        return propertyLinkRegistries.reduce((acc, linkRegistry) => {
            if (!linkRegistry.variables) {
                return acc;
            }
            const variables = JSON.parse(linkRegistry.variables);
            if (!variables) {
                return acc;
            }
            if (!acc.has(variables.locationId)) {
                acc.set(variables.locationId, []);
            }
            acc.get(variables.locationId)?.push(linkRegistry);
            return acc;
        }, new Map<string, Array<LinkRegistry>>());
    }, [propertyLinkRegistries]);


    const locationIdToIssuesAndAssessmentsMap = useMemo<Map<string, Array<Issue | Assessment>>>(() => {
        return [...propertyIssues, ...propertyAssessments].reduce((acc, item) => {
            let locationId: string | null | undefined;

            if ('locationId' in item) {
                locationId = item.locationId;
            } else if ('resourceId' in item) {
                locationId = item.resourceId;
            }

            if (!locationId) {
                return acc;
            }
            if (!acc.has(locationId)) {
                acc.set(locationId, []);
            }
            acc.get(locationId)?.push(item);
            return acc;
        }, new Map<string, Array<Issue | Assessment>>());
    }, [propertyIssues, propertyAssessments]);

    const locationLinkRegistries = locationIdsFilter.map(locationId => locationIdToLinkRegistryMap.get(locationId) ?? []).flat();
    const locationIssuesAndAssessments = locationIdsFilter.map(locationId => locationIdToIssuesAndAssessmentsMap.get(locationId) ?? []).flat();
    const hasLocationItems = locationIssuesAndAssessments.length > 0;
    const hasQrCodes = locationLinkRegistries.length > 0;
    const hasLocationId = locationIdsFilter.length > 0;
    const hasPropertyItems = propertyIssues.length > 0 || propertyAssessments.length > 0;

    if (!hasLocationId) {
        if (hasPropertyItems) {
            return <PropertyHasItemsHiddenByFilter setFilter={setFilter} />;
        }

        return <PropertyHasNoItems />;
    }

    if (hasLocationItems) {
        return <LocationHasItemsHiddenByFilter setFilter={setFilter} />;
    }

    if (hasQrCodes) {
        return <LocationHasNoItemsWithQrCodes />;
    }

    return <LocationHasNoItemsNoQrCodes />;
};

const MessageContainer = styled(Box)<BoxProps>(({ theme }) => ({
    padding: theme.spacing(1)
}));

const PropertyHasItemsHiddenByFilter = ({ setFilter }: PropertyHasItemsHiddenByFilterProps) => (
    <MessageContainer>
        <Typography variant="h6">There are assessments / issues for this property, but they are hidden by filters.</Typography>
        <Button
            sx={{ mt: 1 }}
            variant="contained"
            color="primary"
            onClick={() => setFilter({})}
        >
            Clear Filters
        </Button>
    </MessageContainer>
);

const PropertyHasNoItems = () => (
    <MessageContainer>
        <Typography variant="h6">There are no assessments / issues for this property.</Typography>
    </MessageContainer>
);

const LocationHasNoItemsNoQrCodes = () => (
    <MessageContainer>
        <Typography variant="h6">No assessments / issues have been submitted for this location.</Typography>
        <Typography variant="body1">
            This location does not have any QR codes associated with it. Please scan an unprovisioned QR code you have been provided.
        </Typography>
    </MessageContainer>
);

const LocationHasNoItemsWithQrCodes = () => (
    <MessageContainer>
        <Typography variant="h6">No assessments / issues have been submitted for this location.</Typography>
        <Typography variant="body1">
            This location has QR codes associated with it. To submit assessments, please scan a QR code.
        </Typography>
    </MessageContainer>
);

const LocationHasItemsHiddenByFilter = ({ setFilter }: LocationHasItemsHiddenByFilterProps) => (
    <MessageContainer>
        <Typography variant="h6">There are assessments / issues for this location, but they are hidden by filters.</Typography>
        <Button
            sx={{ mt: 1 }}
            variant="contained"
            color="primary"
            onClick={() => setFilter({})}
        >
            Clear Filters
        </Button>
    </MessageContainer>
);